import React, { useState } from "react";
import * as Form from '@radix-ui/react-form';
import { Button } from '@radix-ui/themes';
import { TextInput } from '@tremor/react';
import { Card, Title, Text } from '@tremor/react';
import { Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from './api.js';
import { BackIcon, ICON_SIZE, SendIcon } from "./actionIcons";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    if (!email) {
      setError("Email is required.");
      setIsLoading(false);
      return;
    }

    try {
      // Replace with your actual API endpoint
      const res = await apiFetch("/reset/forgot-password", { body: { email } });

      if (res.ok) {
        setSubmitted(true);
        setError("");
      } else {
        const data = await res.json();
        setError(data.message || "Something went wrong.");
      }
    } catch (err) {
      setError("Failed to connect to the server.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <Card className="max-w-md mx-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div></div>
          <Title className="text-center">Forgot Password</Title>
          <Button variant="ghost" onClick={() => navigate("/login")}><BackIcon size={ICON_SIZE} />
            Back to Login
          </Button>
        </div>

        {submitted ? (
          <div className="text-center space-y-4 py-4">
            <div className="flex justify-center">
              <CheckCircle size={48} color="green" />
            </div>
            <Text className="text-green-600">
              If that email exists in our system, a reset link has been sent.
            </Text>
            <Text>
              Please check your inbox and follow the instructions to reset your password.
            </Text>
            <Button 
              className="mt-4" 
              onClick={() => navigate("/login")}
            ><BackIcon size={ICON_SIZE} />
              Return to Login
            </Button>
          </div>
        ) : (
          <Form.Root className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Text>
                Enter your email address below and we'll send you instructions to reset your password.
              </Text>
              
              <Form.Field name="email" className="mt-4">
                <div className="flex justify-between">
                  <Form.Label className="text-sm font-medium">Email Address</Form.Label>
                </div>
                <Form.Control asChild>
                  <TextInput
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    error={!!error}
                    icon={Mail}
                  />
                </Form.Control>
                {error && (
                  <div className="flex items-center mt-1 text-sm text-red-500">
                    <AlertCircle size={14} className="mr-1" />
                    <Text color="red">{error}</Text>
                  </div>
                )}
              </Form.Field>
            </div>

            <div className="pt-4">
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading}
              >
                <SendIcon size={ICON_SIZE} />
                {isLoading ? "Sending..." : "Send Reset Link"}
              </Button>
            </div>
          </Form.Root>
        )}
      </Card>
    </div>
  );
}